export type TimeZoneFormat = {
  label: {
    en: string;
    fr: string;
  };
  value: string;
  gmt: string;
};

/**
 * Returns the current UTC offset string for a given IANA timezone identifier,
 * accounting for daylight saving time based on the current date/time.
 *
 * Examples:
 *   getGmtOffset('America/Toronto')  → '(GMT-04:00)' in summer, '(GMT-05:00)' in winter
 *   getGmtOffset('Australia/Sydney') → '(GMT+11:00)' in summer, '(GMT+10:00)' in winter
 */
export function getGmtOffset(ianaTimezone: string): string {
  // Intl.DateTimeFormat with 'timeZoneName: "shortOffset"' returns strings like
  // "GMT-4", "GMT+5:30", "GMT+0" which we normalise into "(GMT±HH:MM)" form.
  const formatter = new Intl.DateTimeFormat('en', {
    timeZone: ianaTimezone,
    timeZoneName: 'shortOffset',
  });

  const parts = formatter.formatToParts(new Date());
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? /* istanbul ignore next */ 'GMT';

  // offsetPart examples: "GMT-4", "GMT+5:30", "GMT+0", "GMT"
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const match = offsetPart.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);

  /* istanbul ignore next */
  if (!match) {
    // Edge case: timezone is exactly UTC (e.g. "GMT")
    return '(GMT+00:00)';
  }

  const sign = match[1];
  const hours = match[2].padStart(2, '0');
  const minutes = (match[3] ?? /* istanbul ignore next */ '00').padStart(2, '0');

  return `(GMT${sign}${hours}:${minutes})`;
}

const usCanadianTimeZones: TimeZoneFormat[] = [
  {
    label: { en: 'Newfoundland Standard Time', fr: 'Heure Normale de Terre-Neuve' },
    value: 'America/St_Johns',
    gmt: getGmtOffset('America/St_Johns'),
  },
  {
    label: { en: 'Atlantic Standard Time', fr: "Heure Normale de l'Atlantique" },
    value: 'America/Halifax',
    gmt: getGmtOffset('America/Halifax'),
  },
  {
    label: { en: 'Eastern Standard Time', fr: "Heure Normale de l'Est" },
    value: 'America/Toronto',
    gmt: getGmtOffset('America/Toronto'),
  },
  {
    label: { en: 'Central Standard Time', fr: 'Heure Normale du Centre' },
    value: 'America/Winnipeg',
    gmt: getGmtOffset('America/Winnipeg'),
  },
  {
    label: { en: 'Mountain Standard Time', fr: 'Heure Normale des Rocheuses' },
    value: 'America/Edmonton',
    gmt: getGmtOffset('America/Edmonton'),
  },
  {
    label: { en: 'Pacific Standard Time', fr: 'Heure Normale du Pacifique' },
    value: 'America/Vancouver',
    gmt: getGmtOffset('America/Vancouver'),
  },
  {
    label: { en: 'Alaska Standard Time', fr: "Heure Normale de l'Alaska" },
    value: 'America/Anchorage',
    gmt: getGmtOffset('America/Anchorage'),
  },
];

const australianTimeZones: TimeZoneFormat[] = [
  {
    label: {
      en: 'Australian Western Standard Time',
      fr: "Heure normale de l'Australie occidentale",
    },
    value: 'Australia/Perth',
    gmt: getGmtOffset('Australia/Perth'),
  },
  {
    label: {
      en: 'Australian Central Standard Time',
      fr: "Heure normale d'Australie centrale",
    },
    value: 'Australia/Adelaide',
    gmt: getGmtOffset('Australia/Adelaide'),
  },
  {
    label: {
      en: 'Australian Eastern Standard Time',
      fr: "Heure normale de l'Est australien",
    },
    value: 'Australia/Sydney',
    gmt: getGmtOffset('Australia/Sydney'),
  },
];

export const timezonesByCountry: Record<string, TimeZoneFormat[]> = {
  US: usCanadianTimeZones,
  CA: usCanadianTimeZones,
  AU: australianTimeZones,
};

export const formatTimeZones: TimeZoneFormat[] = [...usCanadianTimeZones, ...australianTimeZones];
